<template>
  <section>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Confirmar Envío a Comisión</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <p class="mb-3">
          Se ha enviado un código de verificación a su correo
          <strong>{{ email }}</strong
          >.
        </p>

        <p class="mb-4">
          Ingrese el código para confirmar el envío del desempeño a la comisión
          precalificadora.
        </p>

        <b-field label="Código de Verificación">
          <b-input
            v-model="code"
            placeholder="Código de 6 caracteres"
            maxlength="6"
            type="text"
            expanded
          />
        </b-field>

        <b-message type="is-warning" has-icon class="mt-4">
          <strong>Importante:</strong> Una vez confirmado, el desempeño quedará
          en estado "EN PRECALIFICACION" y no podrá modificar las evidencias
          hasta que la comisión complete la evaluación.
        </b-message>

        <b-button
          @click="reenviarCodigo"
          type="is-text"
          class="mt-3"
          :disabled="cooldown > 0"
        >
          {{ cooldown > 0 ? `Reenviar en ${cooldown}s` : "Reenviar código" }}
        </b-button>
      </section>

      <footer class="modal-card-foot actions">
        <div class="actions">
          <button class="button" type="button" @click="$emit('close')">
            Cancelar
          </button>
          <b-button
            type="is-success"
            icon-left="check"
            @click="confirmarEnvio"
            :disabled="!code || code.length !== 6"
            :loading="loading"
          >
            Confirmar Envío
          </b-button>
        </div>
      </footer>
    </div>
    <b-loading v-model="isLoading" is-full-page />
  </section>
</template>

<script>
import axios from "axios";

export default {
  name: "ModalConfirmSendToCommittee",

  props: {
    email: {
      type: String,
      required: true,
    },
    performanceId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      code: "",
      error: "",
      cooldown: 0,
      intervalId: null,
      cooldownSeconds: 60,
      isLoading: false,
      loading: false,
    };
  },

  mounted() {
    const savedTime = localStorage.getItem("cooldownCommittee");
    if (savedTime) {
      const elapsed = Math.floor((Date.now() - parseInt(savedTime)) / 1000);
      const remaining = this.cooldownSeconds - elapsed;
      if (remaining > 0) {
        this.cooldown = remaining;
        this.startCooldownTimer();
      } else {
        localStorage.removeItem("cooldownCommittee");
      }
    }
  },

  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },

  methods: {
    async confirmarEnvio() {
      this.error = "";
      this.loading = true;

      try {
        const response = await axios.post(
          `/performances/${this.performanceId}/confirm_send_to_committee.json`,
          {
            code: this.code,
          }
        );

        this.$buefy.dialog.alert({
          title: "¡Éxito!",
          message:
            "El desempeño ha sido enviado exitosamente a la comisión precalificadora. " +
            "Se ha notificado a los miembros de la comisión para que procedan con la evaluación.",
          type: "is-success",
          hasIcon: true,
          icon: "check-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true,
          onConfirm: () => {
            // Redirigir a la vista de detalle o recargar
            window.location.href = `/performances/process/${response.data.year}/person/${response.data.person_id}`;
          },
        });

        this.$emit("close");
      } catch (error) {
        console.error(error);
        let message = "Hubo un error al confirmar el envío.";

        if (error.response && error.response.data && error.response.data.error) {
          message = error.response.data.error;
        }

        this.$buefy.dialog.alert({
          title: "Error",
          message: message,
          type: "is-danger",
          hasIcon: true,
          icon: "times-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true,
        });
      } finally {
        this.loading = false;
      }
    },

    async reenviarCodigo() {
      this.isLoading = true;

      try {
        // Volver a solicitar el código
        await axios.post(
          `/performances/${this.performanceId}/send_to_committee.json`
        );

        this.$buefy.dialog.alert({
          title: "Código Reenviado",
          message: "Se ha enviado un nuevo código a su correo electrónico.",
          type: "is-success",
          hasIcon: true,
          icon: "check-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true,
        });

        this.cooldown = this.cooldownSeconds;
        localStorage.setItem("cooldownCommittee", Date.now().toString());
        this.startCooldownTimer();
      } catch (error) {
        console.error(error);
        this.$buefy.dialog.alert({
          title: "Error",
          message: "Hubo un error al reenviar el código.",
          type: "is-danger",
          hasIcon: true,
          icon: "times-circle",
          iconPack: "fa",
          ariaRole: "alertdialog",
          ariaModal: true,
        });
      } finally {
        this.isLoading = false;
      }
    },

    startCooldownTimer() {
      if (this.intervalId) clearInterval(this.intervalId);

      this.intervalId = setInterval(() => {
        if (this.cooldown > 0) {
          this.cooldown--;
        } else {
          clearInterval(this.intervalId);
          this.intervalId = null;
          localStorage.removeItem("cooldownCommittee");
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
}
</style>
